import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Check, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscription = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnedSessionId = urlParams.get('session_id');
    if (returnedSessionId) pollPaymentStatus(returnedSessionId);
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { toast.error('Payment verification timeout. Please contact support.'); return; }
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, { headers: getAuthHeaders() });
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        setTimeout(() => { window.location.href = '/subscription'; }, 2000);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) { toast.error('Failed to verify payment status'); }
  };

  const handleSubscribe = async (plan = 'monthly') => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout?plan=${plan}`, { origin_url: originUrl }, { headers: getAuthHeaders() });
      window.location.href = response.data.url;
    } catch (error) { toast.error('Failed to start checkout process'); setLoading(false); }
  };

  const plans = [
    {
      name: 'Free Trial', price: '$0', period: '/month', sub: 'First 30 days free',
      features: ['Weather monitoring', 'Up to 3 fields', 'Basic crop assistant', 'Weather alerts'],
      highlight: false, action: null, label: 'Active Trial',
    },
    {
      name: 'Premium', price: '$27.99', period: '/month', sub: 'Full access to all features',
      tag: 'Popular', tagBg: 'linear-gradient(135deg, var(--gold), var(--gold2))', tagColor: '#0b241a',
      features: ['Unlimited fields', 'Advanced weather forecasts', 'NDVI vegetation monitoring', 'AI crop assistant', 'Weather & vegetation alerts', 'Field activity tracking', 'GDD & rain accumulation', 'Priority support'],
      highlight: true, action: () => handleSubscribe('monthly'), label: 'Subscribe Monthly', tid: 'subscribe-monthly-button',
    },
    {
      name: 'Premium Yearly', price: '$279.90', period: '/year', sub: 'Pay for 10 months, get 12 months',
      tag: 'Best Value - Save 17%', tagBg: 'var(--success)', tagColor: '#fff',
      features: ['Unlimited fields', 'Advanced weather forecasts', 'NDVI vegetation monitoring', 'AI crop assistant', 'Weather & vegetation alerts', 'Field activity tracking', 'GDD & rain accumulation', 'Priority support', '2 months FREE'],
      highlight: false, green: true, action: () => handleSubscribe('yearly'), label: 'Subscribe Yearly', tid: 'subscribe-yearly-button',
    },
  ];

  return (
    <Layout>
      <div data-testid="subscription-page" style={{ maxWidth: 1100 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: 6 }}>Subscription Plans</h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Choose the plan that works best for you</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 20 }}>
          {plans.map(plan => (
            <section key={plan.name} className="card-floating" style={{
              position: 'relative', padding: 24,
              borderColor: plan.highlight ? 'rgba(198,170,88,0.35)' : plan.green ? 'rgba(34,197,94,0.3)' : undefined,
            }}>
              {plan.tag && (
                <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
                  <span style={{ background: plan.tagBg, color: plan.tagColor, padding: '4px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-block' }}>{plan.tag}</span>
                </div>
              )}
              <div style={{ marginBottom: 18 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>{plan.name}</h2>
                <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--gold)' }}>
                  {plan.price}<span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-muted)' }}>{plan.period}</span>
                </div>
                <p style={{ fontSize: 12, color: plan.green ? 'var(--success)' : 'var(--text-muted)', fontWeight: plan.green ? 600 : 400, marginTop: 4 }}>{plan.sub}</p>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {plan.features.map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: f.includes('FREE') ? 'var(--success)' : 'var(--text-secondary)', fontWeight: f.includes('FREE') ? 600 : 400 }}>
                    <Check size={16} style={{ color: plan.highlight ? 'var(--gold)' : plan.green ? 'var(--success)' : 'var(--text-muted)', flexShrink: 0 }} />
                    {f}
                  </li>
                ))}
              </ul>
              {plan.action ? (
                <button onClick={plan.action} disabled={loading} data-testid={plan.tid}
                  className="btn-depth-primary" style={{
                    width: '100%', padding: '12px 18px', fontSize: 14,
                    ...(plan.green ? { background: 'var(--success)', borderColor: 'rgba(0,0,0,0.15)' } : {}),
                    opacity: loading ? 0.5 : 1,
                  }}>
                  <CreditCard size={16} /> {loading ? 'Processing...' : plan.label}
                </button>
              ) : (
                <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>{plan.label}</div>
              )}
            </section>
          ))}
        </div>

        <section className="card-floating" style={{ position: 'relative', padding: 22, textAlign: 'center', marginTop: 28 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Why Choose Premium?</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            Get real-time crop health monitoring, weather alerts tailored to your crops,
            and AI-powered agricultural advice to maximize your harvest.
          </p>
        </section>
      </div>
    </Layout>
  );
};

export default Subscription;
